import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateTenantInput, UpdateTenantInput } from '@bom/shared';
import { api } from '../lib/apiClient';

/**
 * GET /tenants 回的是 PLATFORM#TENANTS 索引项，识别字段是 `tenant_id`（非 `id`）。
 * 故此处用本地型别而非 TenantMeta。
 */
export interface TenantListItem {
  tenant_id: string;
  code: string;
  name: string;
  customer_company: string;
  status: 'active' | 'archived';
  created_at: number;
}

/** 平台管理：產品（GET/POST 需 platform_admin；PATCH 需 platform 或 tenant admin） */
export function useTenants(enabled = true) {
  return useQuery({
    queryKey: ['tenants'],
    queryFn: () => api.get<{ tenants: TenantListItem[] }>('/tenants').then((r) => r.tenants),
    enabled,
  });
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTenantInput) => api.post<{ id: string }>('/tenants', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenants'] });
      qc.invalidateQueries({ queryKey: ['factory-names'] }); // 新填的生产工厂进池，刷新可选项
    },
  });
}

export function useUpdateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { tid: string; body: UpdateTenantInput }) =>
      api.patch<void>(`/tenants/${vars.tid}`, vars.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenants'] });
      qc.invalidateQueries({ queryKey: ['factory-names'] });
    },
  });
}

/** 彻底删除产品及其全部数据（级联，不可逆）。仅平台管理员。 */
export function useDeleteTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tid: string) => api.del<{ deleted: number }>(`/tenants/${tid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenants'] }),
  });
}
